import IPPageNumber from "app/pages/InteractiveProposal/InteractiveProposalDownload/IPPageNuber";
import HexagonImg from "app/pages/InteractiveProposal/images/hexagonImg.png";
import { getSessionStore } from "app/utils/sessionStore";
import { getThemeColors, getUSANumberFormat } from "app/utils/utils";
import { getColorVariations } from "app/utils/colorUtils";
import type { ProjectionLedgerEntry } from "./IRCFundingPage.adapters";

interface IRCProjectionLedgerPageProps {
  ledger: ProjectionLedgerEntry[];
  pageNumber: string | number;
}

const fmt = (v: number) => `$${getUSANumberFormat(v, false)}`;

const surplusStyle = (isSurplus: boolean): React.CSSProperties =>
  isSurplus ? { color: "#166534" } : { color: "#991b1b" };

const headers = [
  "Name",
  "Ownership %",
  "Exposure Value",
  "Life Coverage",
  "Life Surplus/Deficit",
  "Disability Coverage",
  "Disability Surplus/Deficit",
];

const IRCProjectionLedgerPage = ({ ledger, pageNumber }: IRCProjectionLedgerPageProps) => {
  const { branding } = getSessionStore("lookUpResponse");
  const { secondaryColor } = getThemeColors(branding);

  const colorVariations = getColorVariations(secondaryColor, headers.length).reverse();

  return (
    <div className="flex flex-col h-full relative min-h-screen">
      <div className="relative z-20 px-[2em] w-full max-w-[56em] mx-auto p-[1.75em]">
        <h1 className="text-center text-2xl font-bold mb-5 conthrax-font interactive-proposal-text">
          Projection Ledger
        </h1>

        <table className="w-full border-collapse">
          <thead className="text-white">
            <tr>
              {headers.map((h, i) => (
                <th
                  key={i}
                  className="p-1 text-center pdf_table_border border-white font-semibold"
                  style={{ backgroundColor: colorVariations[i], fontSize: "8px" }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ledger.map((entry) => (
              <>
                {/* Plan year group header */}
                <tr key={`year-${entry.planYear}`}>
                  <td
                    colSpan={headers.length}
                    className="p-1 pdf_table_border border-white font-semibold"
                    style={{ backgroundColor: "#D1D5DB", color: "#000000", fontSize: "8px" }}
                  >
                    Plan Year {entry.planYear} ({entry.year})
                  </td>
                </tr>

                {/* Shareholder rows */}
                {entry.rows.map((row) => (
                  <tr
                    key={row.id}
                    style={{ backgroundColor: "#EEEEEE", color: "#000000", fontSize: "8px" }}
                  >
                    <td className="p-1 text-center pdf_table_border border-white">{row.name}</td>
                    <td className="p-1 text-center pdf_table_border border-white">
                      {row.ownershipPercentage}%
                    </td>
                    <td className="p-1 text-center pdf_table_border border-white">
                      {fmt(row.exposureValue)}
                    </td>
                    <td className="p-1 text-center pdf_table_border border-white">
                      {fmt(row.life.current ?? 0)}
                    </td>
                    <td
                      className="p-1 text-center pdf_table_border border-white"
                      style={surplusStyle(row.life.isSurplus)}
                    >
                      {row.life.isSurplus ? "+" : "-"}
                      {fmt(Math.abs(row.life.surplusAmount))}
                    </td>
                    <td className="p-1 text-center pdf_table_border border-white">
                      {fmt(row.disability.current ?? 0)}
                    </td>
                    <td
                      className="p-1 text-center pdf_table_border border-white"
                      style={surplusStyle(row.disability.isSurplus)}
                    >
                      {row.disability.isSurplus ? "+" : "-"}
                      {fmt(Math.abs(row.disability.surplusAmount))}
                    </td>
                  </tr>
                ))}

                {/* Coverage totals row */}
                <tr
                  key={`total-${entry.planYear}`}
                  style={{
                    backgroundColor: "#D1D5DB",
                    color: "#000000",
                    fontSize: "8px",
                    fontWeight: 600,
                  }}
                >
                  <td className="p-1 text-center pdf_table_border border-white">Total</td>
                  <td className="p-1 text-center pdf_table_border border-white">—</td>
                  <td className="p-1 text-center pdf_table_border border-white">
                    {fmt(entry.coverage.totalExposureValue)}
                  </td>
                  <td className="p-1 text-center pdf_table_border border-white">
                    {fmt(entry.coverage.life.total ?? 0)}
                  </td>
                  <td
                    className="p-1 text-center pdf_table_border border-white"
                    style={surplusStyle(entry.coverage.life.isSurplus)}
                  >
                    {entry.coverage.life.isSurplus ? "+" : "-"}
                    {fmt(Math.abs(entry.coverage.life.surplusAmount))}
                  </td>
                  <td className="p-1 text-center pdf_table_border border-white">
                    {fmt(entry.coverage.disability.total ?? 0)}
                  </td>
                  <td
                    className="p-1 text-center pdf_table_border border-white"
                    style={surplusStyle(entry.coverage.disability.isSurplus)}
                  >
                    {entry.coverage.disability.isSurplus ? "+" : "-"}
                    {fmt(Math.abs(entry.coverage.disability.surplusAmount))}
                  </td>
                </tr>
              </>
            ))}
          </tbody>
        </table>
      </div>

      <div className="absolute bottom-0 left-0 w-full">
        <div
          className="h-[350px] bg-cover bg-center"
          style={{ backgroundImage: `url(${HexagonImg})` }}
        />
        <div className="px-[4em] w-full max-w-[56em] mx-auto p-[1.75em] pt-1">
          <IPPageNumber number={pageNumber} />
        </div>
      </div>
    </div>
  );
};

export default IRCProjectionLedgerPage;
